//#region import libraries
import React, { useState, useEffect, useRef } from 'react';
//bootstrap
import { Container, Row, Col, Popover, Overlay, OverlayTrigger, Button, Spinner } from 'react-bootstrap';
//styles
import { Colors, Spacing } from '_styles';
//custom components
import { Screen } from "_atoms";
import { BookingRowItem, CustomModal } from "_molecules";
import { getCodeDescription } from '_utils';
//redux
import { useDispatch, useSelector } from 'react-redux';
import { selectBooking, selectBookingStatus, getConfirmBooking, updateBooking, reset, selectUser, getUser } from '_redux/slices/website/confirm-booking';
//libraries
import { useParams, useHistory } from 'react-router-dom';
import { AiOutlineInfoCircle } from "react-icons/ai";
//#endregion

const getBookingUserId = (booking) => {
    if (booking == null || booking.userId == null) {
        return null;
    }

    if (booking.userId.length == 28) {
        return booking.userId;
    }

    const portalHistory = booking.history?.find(historyItem => historyItem.source?.source == 'PORTAL');
    return portalHistory?.source?.user?.uid || null;
}

const BookingCard = (props) => {
    const ignoredIds = ["SITA", "PORTAL"]; //when userId is sita/portal, it means that the booking was not made by a user and therefore user info should not be fetched
    //#region redux
    const dispatch = useDispatch();
    const booking = useSelector(selectBooking);
    const bookingStatus = useSelector(selectBookingStatus);
    const user = useSelector(selectUser);
    //#endregion

    //#region local state
    // state vars
    const [assistance, setAssistance] = useState([]);
    const [action, setAction] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [successfullyConfirmed, setSuccessfullyConfirmed] = useState(false);
    const [informationHover, setInformationHover] = useState(false);

    // modals
    const [show, setShow] = useState(false);
    const [showAlready, setShowAlready] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [target, setTarget] = useState(null);
    const ref = useRef(null);
    //#endregion

    // url params
    const params = useParams();
    const id = params.id;
    const airport = params.airport

    // navigation
    const history = useHistory();

    //#region useEffect
    // get booking and reset 
    useEffect(() => {
        if (id) {
            dispatch(getConfirmBooking({ 'booking_id': id, 'token': 'confirming-booking' }))
        }
        // initalize reset 
        dispatch(reset());
    }, []);


    useEffect(() => {
        //Check userId
        const userId = getBookingUserId(booking);
        if (userId) {
            dispatch(getUser(({ 'user_id': userId, 'token': booking.id })))
        }
    }, [booking]);

    useEffect(() => {
        if (bookingStatus == "idle") {
            if (id) {
                dispatch(getConfirmBooking({ 'booking_id': id, 'token': 'confirming-booking' }))
            }
        }
    }, [bookingStatus]);

    // set assistance
    useEffect(() => {
        // if booking exsists
        if (booking) {
            // set assistance label and description
            let updates = [];
            booking.assistance.forEach((code) => {
                updates.push({ code, description: getCodeDescription(code) })
            })
            setAssistance(updates);
        }
    }, [booking]);

    // check status of booking confirmation
    useEffect(() => {

        // check for error
        if (bookingStatus == 'failed') {
            // display error message
            setShowError(true)
        }
        // successfully submitted
        if (bookingStatus == 'succeeded' && confirmed == true && action) {
            setShow(true)
            setSuccessfullyConfirmed(true)
        }
    }, [bookingStatus]);
    //#endregion

    // handle booking confirmation
    const handleConfirm = () => {
        setAction(true);
        // check if already confirmed
        if (successfullyConfirmed == true) {
            setShowAlready(true);
            return;
        }
        else {
            // origin airport
            if (booking.flightConfirmationDetails.origin == airport) {
                // already confirmed
                if (booking.originConfirmed == true) {
                    setShowAlready(true)                // booking has already been confirmed
                    setSuccessfullyConfirmed(true)
                    return;
                }
                else {
                    // confirm booking
                    const newBooking = { ...booking, originConfirmed: true }
                    setConfirmed(true)
                    dispatch(updateBooking({ newBooking, token: 'confirming-booking' }))
                }
            }
            // departure airport
            else if (booking.flightConfirmationDetails.destination == airport) {
                // already confirmed
                if (booking.destinationConfirmed == true) {
                    setShowAlready(true)
                    setSuccessfullyConfirmed(true)
                    return;
                }
                // departure airport not confirmed yet
                else {
                    const newBooking = { ...booking, destinationConfirmed: true }
                    setConfirmed(true)
                    dispatch(updateBooking({ newBooking, token: 'confirming-booking' }))
                }
            }
            else {
                // incorrect airport in url
                setShowError(true);
            }
        }
    }

    const popover = (
        <Popover id="popover-basic">
            <Popover.Header style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, color: 'white' }} as="h3">PRM Portal</Popover.Header>
            <Popover.Body>
                Information such as passenger <strong>due time</strong> are availble on the PRM Assist Portal.
                Click to find out more.
            </Popover.Body>
        </Popover>
    );

    const handleHover = (e) => {
        setInformationHover(true)
        setTarget(e.target)
        setShowInfo(true)
    }

    return (
        <Screen loading={bookingStatus == 'loading' ? true : false}>
            <Container fluid={true}>

                <Row>

                    <Col className="vh-100">

                        {/* Title */}
                        <Container className="mt-5">
                            <p style={{ fontSize: 26, fontWeight: 'bold' }}>Booking Confirmation</p>

                            <div className="mt-3" style={{}}>
                                <p style={{ fontSize: 18, fontWeight: '600', opacity: 0.6 }}>Please review the following flight information and approve the passengers assistance request.</p>
                            </div>

                        </Container>

                        {/* Card */}
                        <Container className="mt-5" >

                            <Container style={{ width: '70%', borderRadius: 10, boxShadow: "0px 4px 8px 0px #9E9E9E", }}>
                                {
                                    <>
                                        {/* Title */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <p style={{ fontSize: 22, fontWeight: 'bold', marginTop: Spacing.SCALE_16, }}>Booking Information</p>
                                        </div>

                                        {/* Number, Date, Due Time & Reference Number */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: Spacing.SCALE_12 }}>

                                            <BookingRowItem title={"Flight Number"} subtitle={booking == null ? " " : booking.flightConfirmationDetails.carrierCode + booking.flightConfirmationDetails.flightNumber} />
                                            <BookingRowItem title={"Date"} subtitle={booking == null ? " " : booking.flightConfirmationDetails.departureDate} />

                                            {/* Due Time */}
                                            <div>
                                                <OverlayTrigger trigger="hover" placement="right" overlay={popover}>
                                                    <div onClick={() => { history.push("/register") }} onMouseOver={handleHover} onMouseLeave={() => { setInformationHover(false) }} style={{ cursor: 'pointer' }}>
                                                        <div style={{ display: 'flex' }}>
                                                            <p style={{ fontWeight: 'bold', opacity: 0.6 }}>Due Time</p>
                                                            <AiOutlineInfoCircle size={21} style={{ padding: 2 }} color={'#307FE2'} />
                                                        </div>
                                                        <p style={{ fontWeight: '300', marginTop: -Spacing.SCALE_12, textDecoration: informationHover ? 'underline' : 'none', color: informationHover ? Colors.PRM_LIGHT_BLUE : " " }}>PRM Portal</p>
                                                    </div>
                                                </OverlayTrigger>
                                                <Overlay
                                                    show={showInfo}
                                                    target={target}
                                                    placement="bottom"
                                                    container={ref}
                                                    containerPadding={20}
                                                >
                                                    <Popover id="popover-contained">
                                                        <Popover.Header as="h3">Popover bottom</Popover.Header>
                                                        <Popover.Body>
                                                            <strong>Holy guacamole!</strong> Check this info.
                                                        </Popover.Body>
                                                    </Popover>
                                                </Overlay>

                                            </div>
                                            <BookingRowItem title={"Reference Number"} subtitle={booking == null ? " " : booking.referenceNumber} />
                                        </div>

                                        {/* Airports */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <BookingRowItem title={"Departure Airport"} subtitle={booking == null ? " " : booking.flightConfirmationDetails == undefined ? " " : booking.flightConfirmationDetails.origin} />
                                            <BookingRowItem title={"Status"} subtitle={booking == null ? " " : booking.originConfirmed ? "Confirmed" : "Not Confirmed"} />
                                            <BookingRowItem title={"Arrival Airport"} subtitle={booking == null ? " " : booking.flightConfirmationDetails == undefined ? " " : booking.flightConfirmationDetails.destination} />
                                            <BookingRowItem title={"Status"} subtitle={booking == null ? " " : booking.destinationConfirmed ? "Confirmed" : "Not Confirmed"} />
                                        </div>

                                        {/* Assistance Requested */}
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ flex: 0.7 }}>
                                                <p style={{ fontWeight: 'bold', opacity: 0.6 }}>Assistance Requested</p>
                                                {
                                                    (assistance == null || assistance == undefined) ?
                                                        " "
                                                        :
                                                        assistance.map(assistance => {
                                                            return (
                                                                <div style={{ marginTop: Spacing.SCALE_12, display: 'flex' }}>
                                                                    <div style={{ borderRadius: 25, width: 10, height: 10, backgroundColor: Colors.PRM_LIGHT_BLUE }} />
                                                                    <p style={{ marginTop: -Spacing.SCALE_8, fontWeight: '300', marginLeft: Spacing.SCALE_8 }}>{assistance.description}  -  </p>
                                                                    <p style={{ marginTop: -Spacing.SCALE_8, fontWeight: 'bold', color: Colors.PRM_LIGHT_BLUE }}>{assistance.code}</p>
                                                                </div>
                                                            )
                                                        })}
                                            </div>
                                            {/* User Info */}
                                            <div>
                                                <div>
                                                    <BookingRowItem title={"Passenger Name"} subtitle={booking && booking.name ? booking.name : ""} />
                                                    <BookingRowItem title={"Passenger Email"} subtitle={user == null ? " " : user.email} />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                }

                            </Container>

                            {/* Confirm Booking */}
                            <Container className="mt-5 justify-content-md-center d-inline-flex">
                                <Button
                                    variant={(bookingStatus == 'succeeded' && confirmed == true) ? "outline-success" : (bookingStatus == 'failed' && confirmed == true) || (booking == null) ? "outline-danger" : successfullyConfirmed == true ? "outline-success" : "outline-primary"}
                                    onClick={handleConfirm} loading={true} style={{ width: 175 }}
                                    disabled={booking == null ? true : false}
                                >

                                    {
                                        bookingStatus == 'failed' && confirmed == true ?
                                            'Confirmation Failed'
                                            :
                                            successfullyConfirmed == true ?
                                                'Sucessfully Confirmed'
                                                :
                                                booking == null ?
                                                    'Error Loading Booking'
                                                    :
                                                    confirmed == false ?
                                                        'Confirm Booking'
                                                        :
                                                        bookingStatus == 'succeeded' ?
                                                            'Sucessfully Confirmed'
                                                            :
                                                            (
                                                                <>
                                                                    <Spinner
                                                                        as="span"
                                                                        animation="border"
                                                                        size="sm"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                    />
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </>
                                                            )
                                    }
                                </Button>
                            </Container>

                        </Container>

                    </Col>

                </Row>

            </Container>


            {/* Success Modal */}
            <CustomModal
                hide={() => setShow(false)}
                show={show}
                title={"Successfully Confirmed Booking"}
                body={"The assistance booking has been sucessfully confirmed and the passenger has been notified."}
                subbody={"Thank you for using PRM Assist."}
                button={"Done"}
                openButton={() => setShow(false)}
            />

            {/* Already Confirmed Modal */}
            <CustomModal
                hide={() => setShowAlready(false)}
                show={showAlready}
                title={"Already Confirmed Booking"}
                body={"This assistance booking has already been approved by your airport and the passenger has been notified"}
                subbody={"Thank you for using PRM Assist."}
                button={"Done"}
                openButton={() => setShowAlready(false)}
            />

            {/* Error Modal */}
            <CustomModal
                hide={() => setShowError(false)}
                show={showError}
                title={"Error Confirming Booking"}
                body={"Unfortunately, we ran into an issue trying to confirm the booking."}
                subbody={"Please try again or contact us."}
                button={"Done"}
                openButton={() => setShowError(false)}
            />
        </Screen>

    );
};


export default BookingCard;
